import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  ChipProps,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { releaseNotesApi } from '../services/api';
import { Release } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import { CardSkeleton } from '../components/SkeletonLoaders';
import ErrorMessage from '../components/ErrorMessage';
import EmptyState from '../components/EmptyState';
import { useApiQuery } from '../hooks/useApiQuery';

const SECTION_CONFIG: Record<string, { label: string; color: ChipProps['color'] }> = {
  added: { label: 'Neu', color: 'success' },
  changed: { label: 'Geaendert', color: 'info' },
  fixed: { label: 'Behoben', color: 'warning' },
  security: { label: 'Sicherheit', color: 'error' },
};

function formatDate(iso: string): string {
  return formatDateSafe(iso, 'd. MMMM yyyy');
}

interface ReleaseSectionProps {
  sectionKey: string;
  items: string[];
}

function ReleaseSection({ sectionKey, items }: ReleaseSectionProps) {
  const config = SECTION_CONFIG[sectionKey] || { label: sectionKey, color: 'default' as const };

  return (
    <Box sx={{ mb: 2 }}>
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        sx={{ mb: 1, fontWeight: 600 }}
      />
      <List dense disablePadding>
        {items.map((item, idx) => {
          const isIndented = item.startsWith('  ');
          const isBold = item.startsWith('**') && item.endsWith('**');

          if (isBold) {
            return (
              <ListItem key={idx} sx={{ py: 0.25 }}>
                <ListItemText
                  primary={item.replace(/\*\*/g, '')}
                  primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 600 }}
                />
              </ListItem>
            );
          }

          return (
            <ListItem key={idx} sx={{ py: 0.25, pl: isIndented ? 4 : 2 }}>
              <ListItemText
                primary={`\u2022 ${item.trim()}`}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}

interface ReleaseCardProps {
  release: Release;
  defaultExpanded: boolean;
}

function ReleaseCard({ release, defaultExpanded }: ReleaseCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const sectionKeys = Object.keys(release.sections);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      data-testid={`release-${release.version}`}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span">
            v{release.version}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDate(release.date)}
          </Typography>
          {release.title && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {release.title}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {sectionKeys.map((key) => {
              const config = SECTION_CONFIG[key] || { label: key, color: 'default' as const };
              return (
                <Chip
                  key={key}
                  label={config.label}
                  color={config.color}
                  size="small"
                  variant="outlined"
                />
              );
            })}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {sectionKeys.map((key) => (
          <ReleaseSection key={key} sectionKey={key} items={release.sections[key]} />
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

export default function ChangelogPage() {
  const { data, loading, error, refetch } = useApiQuery(
    () => releaseNotesApi.list(),
    []
  );

  const releases = data?.releases || [];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Neuigkeiten
      </Typography>

      {error && (
        <ErrorMessage
          message={error}
          title="Fehler beim Laden der Release-Notes"
          onRetry={refetch}
        />
      )}

      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="changelog-skeleton">
          {Array.from({ length: 3 }).map((_, i) => (
            <CardSkeleton key={i} lines={4} />
          ))}
        </Box>
      )}

      {!loading && !error && releases.length === 0 && (
        <EmptyState
          variant="empty"
          title="Keine Release-Notes vorhanden"
          description="Es wurden noch keine Versionen veroeffentlicht."
        />
      )}

      {!loading && !error && releases.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {releases.map((release, idx) => (
            <ReleaseCard
              key={release.version}
              release={release}
              defaultExpanded={idx === 0}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
